import { useEffect, useRef, useState } from "react";
import { LoaderCircleIcon, UploadIcon, XIcon } from "lucide-react";

import { ExploreImageChip } from "@/components/tools/ExploreImageChip";
import { ExploreImageKeywordInput } from "@/components/tools/ExploreImageKeywordInput";
import { ExploreImagePanelLabel } from "@/components/tools/ExploreImagePanelLabel";
import { ExploreImageStageButton } from "@/components/tools/ExploreImageStageButton";
import { DialogTitle } from "@/components/ui/dialog";

import { useSuggestExploreImageDetails } from "@/hooks/mutations/useSuggestExploreImageDetails";
import { useUploadExploreImage } from "@/hooks/mutations/useUploadExploreImage";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { positionCategoryToLabel } from "@/lib/constants";
import {
  EXPLORE_IMAGE_CAPTION_MAX_LENGTH,
  EXPLORE_IMAGE_MIN_WIDTH,
  EXPLORE_IMAGE_STAGE_MAX_HEIGHT,
  LOW_RES_EXPLORE_IMAGE_WIDTH,
  chamferClipPath,
  exploreImageIndustryToLabel,
  formatExploreImageSize,
} from "@/lib/exploreImages";
import { cn } from "@/lib/utils";
import { ExploreImageIndustry, PositionCategory } from "@/types";

const toggle = <T,>(values: T[], value: T) =>
  values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value];

// One file of an upload batch, laid out like the lightbox: the photo on the
// stage, and every detail it needs in the side panel. Guac's model fills the
// details in first, for the admin to check; nothing is uploaded until all of
// them are set.
export const ExploreImageUploadForm = ({
  file,
  index,
  onClose,
  onDone,
  total,
}: {
  file: File;
  index: number;
  onClose: () => void;
  onDone: () => void;
  total: number;
}) => {
  /* Database CRUD Operations */
  // Create
  const uploadExploreImage = useUploadExploreImage();
  const suggestDetails = useSuggestExploreImageDetails();

  /* React Hooks */
  const [caption, setCaption] = useState("");
  const [positionCategories, setPositionCategories] = useState<
    PositionCategory[]
  >([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [industries, setIndustries] = useState<ExploreImageIndustry[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );

  const showSuccessToast = useSuccessToast();
  const showErrorToast = useErrorToast();

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Each file gets one model call; the ref stops StrictMode's second effect
  // run from making another.
  const suggestedFileRef = useRef<File | null>(null);
  const { mutate: suggest } = suggestDetails;
  useEffect(() => {
    if (suggestedFileRef.current === file) return;
    suggestedFileRef.current = file;
    suggest(file, {
      onSuccess: (suggestion) => {
        setCaption(
          suggestion.caption.slice(0, EXPLORE_IMAGE_CAPTION_MAX_LENGTH),
        );
        setPositionCategories(suggestion.positionCategories);
        setKeywords(suggestion.keywords);
        setIndustries(suggestion.industries);
      },
    });
  }, [file, suggest]);

  /* Helpers */
  const missing = [
    { label: "caption", isMissing: !caption.trim() },
    { label: "category", isMissing: !positionCategories.length },
    { label: "keywords", isMissing: !keywords.length },
  ]
    .filter((field) => field.isMissing)
    .map((field) => field.label);
  const isTooSmall = !!size && size.width < EXPLORE_IMAGE_MIN_WIDTH;
  const isLowRes = !!size && size.width < LOW_RES_EXPLORE_IMAGE_WIDTH;
  const isLast = index === total - 1;
  const isProcessing = suggestDetails.isPending;
  const poorFitReason = suggestDetails.data?.poorFitReason ?? null;
  const canUpload =
    !!size &&
    !isTooSmall &&
    !missing.length &&
    !isProcessing &&
    !uploadExploreImage.isPending;
  const uploadButtonLabel = isLast ? "Upload" : "Upload & next";

  /* Handlers */
  const handleUpload = () => {
    uploadExploreImage.mutate(
      {
        image: file,
        positionCategories,
        caption: caption.trim(),
        keywords,
        industries,
      },
      {
        onSuccess: () => {
          showSuccessToast({ description: `Uploaded ${file.name}.` });
          onDone();
        },
        onError: (error) => {
          // Surfaces guac's reason, e.g. an image under the minimum width.
          const message = (
            error as { response?: { data?: { message?: string } } }
          ).response?.data?.message;
          showErrorToast({
            description: `Failed to upload ${file.name}${message ? `: ${message}` : "."}`,
          });
        },
      },
    );
  };

  /* Render */
  return (
    <div className="grid min-h-full grid-rows-[auto_auto] lg:h-full lg:grid-rows-[auto_minmax(0,1fr)]">
      <DialogTitle className="sr-only">Upload {file.name}</DialogTitle>

      {/* Top bar */}
      <div className="flex h-14 items-center gap-4 border-b border-black/10 px-5 dark:border-white/10">
        <span className="font-plex-mono text-[11px] uppercase tracking-[0.16em] text-[#77787A]">
          Upload
        </span>
        <span className="font-plex-mono text-xs tracking-[0.16em] text-[#D17949] dark:text-[#F39562]">
          {String(index + 1).padStart(3, "0")}
          <span className="text-[#77787A]">
            {" "}
            / {String(total).padStart(3, "0")}
          </span>
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-[#4C4F52] dark:text-[#DDDDDD]">
          {file.name}
        </span>
        <ExploreImageStageButton onClick={onClose} title="Close (Esc)">
          <XIcon className="h-5 w-5" />
        </ExploreImageStageButton>
      </div>

      <div className="grid lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_480px] lg:grid-rows-[minmax(0,1fr)]">
        {/* Stage */}
        <div className="relative flex h-[calc(100dvh-56px)] flex-col bg-[radial-gradient(ellipse_at_center,#F0F0F0_0%,#DDDDDD_75%)] dark:bg-[radial-gradient(ellipse_at_center,#1C1D21_0%,#0E0F11_70%)] lg:h-auto lg:min-h-0">
          <div className="flex min-h-0 flex-1 items-center justify-center px-16 py-6">
            {previewUrl && (
              <img
                alt=""
                className={cn(
                  "block max-w-full shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)]",
                  EXPLORE_IMAGE_STAGE_MAX_HEIGHT,
                )}
                onLoad={(e) => {
                  const { naturalHeight, naturalWidth } = e.currentTarget;
                  setSize({ width: naturalWidth, height: naturalHeight });
                }}
                src={previewUrl}
              />
            )}
          </div>

          {isProcessing ? (
            <div className="pointer-events-none absolute inset-x-0 top-6 flex justify-center">
              <span
                className="flex items-center gap-2 bg-[#212327]/85 px-4 py-2 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-white backdrop-blur"
                style={{ clipPath: chamferClipPath(8) }}
              >
                <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#F39562]" />
                Running through image processor
              </span>
            </div>
          ) : null}

          <div className="flex h-12 shrink-0 items-center gap-3 border-t border-black/10 bg-[#FAFAFA] px-5 dark:border-white/10 dark:bg-[#0E0F11]">
            {size ? (
              <span className="shrink-0 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-[#212327] dark:text-white">
                {size.width} × {size.height}
              </span>
            ) : null}
            <span className="shrink-0 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-[#77787A] dark:text-[#9E9E9E]">
              {formatExploreImageSize(file.size)} original
            </span>
            {isTooSmall ? (
              <span className="shrink-0 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-red-600 dark:text-red-400">
                Too small · guac needs {EXPLORE_IMAGE_MIN_WIDTH}px wide
              </span>
            ) : null}
            {isLowRes && !isTooSmall ? (
              <ExploreImageChip
                label="Low res · may look soft on 3x phones"
                size="sm"
                tone="copper"
              />
            ) : null}
            <span className="ml-auto hidden min-w-0 truncate text-xs text-[#77787A] xl:inline">
              Guac saves an optimized WebP and finds the focus on upload. Adjust
              it after in the gallery.
            </span>
          </div>
        </div>

        {/* Side panel */}
        <div className="flex flex-col gap-7 border-t border-black/10 bg-white p-6 dark:border-white/10 dark:bg-[#141518] lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0">
          {isProcessing ? (
            <div className="flex items-center gap-2 font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#77787A]">
              <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#D17949] dark:text-[#F39562]" />
              Reading the photo to fill in its details…
            </div>
          ) : suggestDetails.isSuccess ? (
            <div className="flex flex-col gap-2">
              <span className="font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#77787A]">
                Auto-filled · check before uploading
              </span>
              {poorFitReason ? (
                <ExploreImageChip
                  label={`May not suit a job card · ${poorFitReason}`}
                  size="sm"
                  tone="copper"
                />
              ) : null}
            </div>
          ) : suggestDetails.isError ? (
            <span className="font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#77787A]">
              Couldn&apos;t auto-fill · fill in the details by hand
            </span>
          ) : null}

          <fieldset
            className="flex flex-col gap-7 transition-opacity disabled:opacity-40"
            disabled={isProcessing}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <ExploreImagePanelLabel>Caption</ExploreImagePanelLabel>
                <span className="font-plex-mono text-[10px] text-[#77787A]">
                  {caption.length}/{EXPLORE_IMAGE_CAPTION_MAX_LENGTH}
                </span>
              </div>
              <textarea
                autoFocus
                className="min-h-[72px] resize-none bg-black/[0.03] px-3 py-2 text-sm text-[#212327] outline-none ring-1 ring-inset ring-black/10 transition-shadow placeholder:text-[#9E9E9E] focus:ring-[#D17949] dark:bg-white/[0.04] dark:text-white dark:ring-white/10 dark:placeholder:text-[#77787A] dark:focus:ring-[#D17949]"
                maxLength={EXPLORE_IMAGE_CAPTION_MAX_LENGTH}
                onChange={(e) => {
                  setCaption(e.target.value);
                }}
                placeholder="Welder in a hood, sparks, dark shop…"
                value={caption}
              />
            </div>

            <div className="flex flex-col gap-2">
              <ExploreImagePanelLabel>Categories</ExploreImagePanelLabel>
              <div className="flex flex-wrap gap-1.5">
                {Object.values(PositionCategory).map((category) => (
                  <ExploreImageChip
                    key={category}
                    label={positionCategoryToLabel[category]}
                    onClick={() => {
                      setPositionCategories((current) =>
                        toggle(current, category),
                      );
                    }}
                    tone={
                      positionCategories.includes(category)
                        ? "active"
                        : "outline"
                    }
                  />
                ))}
              </div>
            </div>

            <ExploreImageKeywordInput
              keywords={keywords}
              onChange={setKeywords}
            />

            <div className="flex flex-col gap-2">
              <ExploreImagePanelLabel>Industries</ExploreImagePanelLabel>
              <div className="flex flex-wrap gap-1.5">
                {Object.values(ExploreImageIndustry).map((industry) => (
                  <ExploreImageChip
                    key={industry}
                    label={exploreImageIndustryToLabel[industry]}
                    onClick={() => {
                      setIndustries((current) => toggle(current, industry));
                    }}
                    size="sm"
                    tone={industries.includes(industry) ? "active" : "outline"}
                  />
                ))}
              </div>
            </div>
          </fieldset>

          <div className="mt-auto flex flex-col gap-3 border-t border-black/10 pt-5 dark:border-white/10">
            {missing.length ? (
              <span className="font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#77787A]">
                Needs{" "}
                <span className="text-[#D17949] dark:text-[#F39562]">
                  {missing.join(" · ")}
                </span>
              </span>
            ) : null}
            <div className="flex gap-2">
              <button
                className="px-4 py-2.5 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#77787A] ring-1 ring-inset ring-black/15 transition-colors hover:text-[#212327] disabled:opacity-30 dark:text-[#9E9E9E] dark:ring-white/15 dark:hover:text-white"
                disabled={uploadExploreImage.isPending}
                onClick={onDone}
                style={{ clipPath: chamferClipPath(8) }}
                type="button"
              >
                Skip
              </button>
              <button
                className="flex flex-1 items-center justify-center gap-2 bg-[#D17949] py-2.5 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-[#FFF5F0] transition-colors hover:bg-[#F39562] disabled:bg-black/10 disabled:text-black/30 dark:disabled:bg-white/10 dark:disabled:text-white/30"
                disabled={!canUpload}
                onClick={handleUpload}
                style={{ clipPath: chamferClipPath(8) }}
                type="button"
              >
                <UploadIcon className="h-4 w-4" />
                {uploadExploreImage.isPending
                  ? "Uploading…"
                  : isProcessing
                    ? "Processing…"
                    : uploadButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
